import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { MessageSquare, PlusCircle } from 'lucide-react';
import ConversationList from '@/components/messaging/ConversationList';
import { MessageThread } from '@/components/messaging/MessageThread';
import NewConversationDialog from '@/components/messaging/NewConversationDialog';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/lib/supabase';
import { StandardPageHeader } from '@/components/layout/StandardPageHeader';
import { toast } from 'sonner';
import { useQueryClient } from '@tanstack/react-query';


const Messages: React.FC = () => {
  const {
    conversationId
  } = useParams<{
    conversationId: string;
  }>();
  const [activeConversation, setActiveConversation] = useState<string | undefined>(conversationId);
  const navigate = useNavigate();
  const {
    user
  } = useAuth();
  const isAdminRoute = window.location.pathname.includes('/admin');
  const queryClient = useQueryClient();

  useEffect(() => {
    setActiveConversation(conversationId);
  }, [conversationId]);

  const handleSelectConversation = (id: string) => {
    const route = isAdminRoute ? `/admin/messages/${id}` : `/messages/${id}`;
    navigate(route);
  };

  const handleNewConversationSuccess = (id: string) => {
    const route = isAdminRoute ? `/admin/messages/${id}` : `/messages/${id}`;
    navigate(route);
    toast.success('New conversation created');
    
    // Force refresh conversations list
    queryClient.invalidateQueries({ queryKey: ['conversations'] });
  };

  // Mark messages as read when viewing a conversation
  useEffect(() => {
    if (conversationId && user) {
      const markMessagesAsRead = async () => {
        try {
          // Get all unread messages in this conversation sent by others
          const { data: messages, error } = await supabase
            .from('messages')
            .select('id')
            .eq('conversation_id', conversationId)
            .neq('sender_id', user.id);
          
          if (error) {
            console.error('Error fetching messages to mark as read:', error);
            throw error;
          }
          
          if (messages && messages.length > 0) {
            // Check which messages are not already read
            const messageIds = messages.map(m => m.id);
            const { data: existingReads } = await supabase
              .from('message_reads')
              .select('message_id')
              .eq('user_id', user.id)
              .in('message_id', messageIds);
            
            // Filter out messages that are already read
            const readMessageIds = existingReads?.map(r => r.message_id) || [];
            const unreadMessageIds = messageIds.filter(id => !readMessageIds.includes(id));
            
            // Mark unread messages as read
            if (unreadMessageIds.length > 0) {
              const readEntries = unreadMessageIds.map(messageId => ({
                message_id: messageId,
                user_id: user.id,
              }));
              
              const { error: insertError } = await supabase
                .from('message_reads')
                .upsert(readEntries, { onConflict: 'message_id,user_id' });
              
              if (insertError) {
                console.error('Error marking messages as read:', insertError);
                throw insertError;
              }
              
              // Update conversations list after marking messages as read
              queryClient.invalidateQueries({ queryKey: ['conversations'] });
            }
          }
        } catch (error) {
          console.error('Error marking messages as read:', error);
          toast.error('Failed to update message status');
        }
      };
      
      markMessagesAsRead();
    }
  }, [conversationId, user, queryClient]);

  return (
    <>
      <StandardPageHeader
        title="Messages"
        subtitle="Communicate with families and staff"
        icon={MessageSquare}
        actions={
          <NewConversationDialog onSuccess={handleNewConversationSuccess}>
            <Button>
              <PlusCircle className="h-4 w-4 mr-2" />
              New Message
            </Button>
          </NewConversationDialog>
        }
      />

      <div className="px-4 md:px-6 py-6">
        <div className="flex flex-col lg:flex-row gap-6 h-[calc(100vh-12rem)]">
          {/* Left: Conversation List */}
          <div className="w-full lg:w-96 xl:w-[28rem] h-full flex-shrink-0 flex flex-col bg-white/50 backdrop-blur-sm rounded-xl border border-gray-100/60 shadow-sm overflow-hidden">
            <ConversationList activeConversationId={activeConversation} onSelectConversation={handleSelectConversation} />
          </div>
          {/* Right: Message Thread */}
          <div className="flex-1 h-full flex flex-col bg-white/50 backdrop-blur-sm rounded-xl border border-gray-100/60 shadow-sm overflow-hidden">
            {conversationId ? <MessageThread conversationId={conversationId} /> : (
              <div className="flex items-center justify-center h-full text-muted-foreground">
                Select a conversation or create a new one
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default Messages;
